import {
  faCamera,
  faChevronDown,
  faChevronRight,
  faClipboardList,
  faComments,
  faCopy,
  faEllipsisVertical,
  faEye,
  faFolder,
  faListCheck,
  faMapLocation,
  faMapLocationDot
} from '@fortawesome/free-solid-svg-icons';
import { getEntityId } from 'helpers/entityId';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Badge from 'components/base/Badge';
import Button from 'components/base/Button';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Card, Col, Dropdown, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { ChangeJobStageModal } from 'components/modals/ChangeJobStageModal';
import DuplicateJobModal from 'components/modals/DuplicateJobModal';
import HumanReadableTime from 'components/utils/HumanReadableTime';
import { JOB_STAGES, STAGE_MAP } from 'constants/jobStage';
import { getCardStyleForReturnedEquipment } from 'helpers/jobUtils';
import { shortenText } from 'helpers/utils';
import { useGlobalSocket } from 'hooks/useGlobalSocket';
import TaskModal from 'pages/apps/tasks/components/TaskModal';
import { JobOption } from 'pages/apps/tasks/types/formFields';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { useJobs } from 'providers/JobContext';
import {
  CommentType,
  GET_COMMENTS_WITH_REPLIES_BY_JOB,
  GET_STICKY_COMMENTS_BY_JOB
} from 'services/api/comment';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import {
  GET_LATEST_POST_JOB_REPORT,
  JobComment,
  TOGGLE_WATCH_JOB,
  UPDATE_JOB_PRIORITY,
  UPDATE_JOB_STAGE
} from 'services/api/job';
import { GET_FILTERED_TASKS } from 'services/api/task';
import DateFormatter from '../project-details/DateFormatter';
import SharePointModal from '../sharepoint/SharePointModal';
import CardViewModal from './CardViewModal';
import CustomerView from './CustomerView';
import ImageUploadModal from './JobImageUploadModal';
import StickyComments from './StickyComments';
import { CardViewItemProps } from './types/types';
import { ToolWorkOrderModal } from '../modals/ToolWorkOrderModal';
import PostJobReportModal from 'pages/apps/post-job-report';
import '../../../../styles/jobCards.css';

const structureComments = (flatComments: JobComment[]): JobComment[] => {
  const commentMap = new Map<string, JobComment>();
  const rootComments: JobComment[] = [];

  flatComments.forEach(comment => {
    commentMap.set(getEntityId(comment) || '', { ...comment, replies: [] });
  });

  flatComments.forEach(comment => {
    const commentWithReplies = commentMap.get(getEntityId(comment) || '');
    if (!commentWithReplies) return;

    if (comment.parentCommentId) {
      const parent = commentMap.get(comment.parentCommentId);
      if (parent) {
        if (!parent.replies) parent.replies = [];
        parent.replies.push(commentWithReplies);
      }
    } else {
      rootComments.push(commentWithReplies);
    }
  });

  return rootComments;
};

const getTotalCommentCount = (comments: JobComment[]): number =>
  comments.reduce((total, comment) => {
    const repliesCount = comment.replies
      ? getTotalCommentCount(comment.replies)
      : 0;
    return total + 1 + repliesCount;
  }, 0);

const getAllComments = (comments: JobComment[]): JobComment[] => {
  const result: JobComment[] = [];
  comments.forEach(comment => {
    result.push(comment);
    if (comment.replies && comment.replies.length > 0) {
      result.push(...getAllComments(comment.replies));
    }
  });
  return result;
};

const CardViewItem: React.FC<CardViewItemProps> = ({
  project,
  JobStages,
  getJobStage,
  canUpdateJobStage = false,
  canUpdateMarkClosed = false,
  canUpdateJob = false,
  canAddCommentsField = false,
  canAddShopCommentsField = false,
  canAddJobStageCommentsField = false
}) => {
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
  const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
  const [scrollToTasks, setScrollToTasks] = useState<boolean>(false);
  const [openImageUpload, setOpenImageUpload] = useState<boolean>(false);
  const [openSharePoint, setOpenSharePoint] = useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState<boolean>(false);
  const [showStageModal, setShowStageModal] = useState<boolean>(false);
  const [comments, setComments] = useState<JobComment[]>([]);
  const [expandedComment, setExpandedComment] = useState<boolean>(false);
  const [openToolWorkOrder, setOpenToolWorkOrder] = useState<boolean>(false);
  const [openPostJobReport, setOpenPostJobReport] = useState<boolean>(false);

  const { authUser } = useAccessToken();
  const { toggleJobPriority } = useJobs();
  const queryClient = useQueryClient();

  const projectId = (project as any).id || project._id;

  const watchedByCurrentUser = useMemo(() => {
    const currentUserId = (authUser as any)?.id || authUser?._id;
    if (!currentUserId) return false;
    const watchers = (project.watchers ?? []) as Array<
      string | { _id?: string; id?: string }
    >;
    return watchers.some(watcher => {
      if (!watcher) return false;
      return typeof watcher === 'string'
        ? watcher === currentUserId
        : (watcher.id || watcher._id) === currentUserId;
    });
  }, [authUser, project.watchers]);

  const [isWatching, setIsWatching] = useState(watchedByCurrentUser);

  useEffect(() => {
    setIsWatching(watchedByCurrentUser);
  }, [watchedByCurrentUser]);

  const { data: fetchedComments, refetch: refetchComments } = useQuery(
    ['jobComments', projectId],
    () => GET_COMMENTS_WITH_REPLIES_BY_JOB(projectId as string),
    {
      enabled: !!projectId,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      onSuccess: data => {
        if (data?.data && Array.isArray(data.data)) {
          const sorted = [...data.data].sort(
            (a, b) =>
              new Date(b.createdAt || 0).getTime() -
              new Date(a.createdAt || 0).getTime()
          );
          setComments(sorted);
        }
      }
    }
  );

  const currentComments = fetchedComments?.data || comments;

  const structuredComments = useMemo(
    () => structureComments(currentComments),
    [currentComments]
  );

  const totalCommentCount = useMemo(
    () => getTotalCommentCount(structuredComments),
    [structuredComments]
  );

  const latestComment = useMemo(() => {
    const all = getAllComments(structuredComments);
    if (all.length === 0) return null;
    return all.sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    )[0];
  }, [structuredComments]);

  const { data: employees } = useQuery(
    'get_all_employees_for_task',
    GET_EMPLOYEES_QUERY,
    {
      staleTime: 3 * 60 * 60 * 1000,
      enabled: showTaskModal,
      select: (response: any) =>
        response.data.map((employee: any) => ({
          value: getEntityId(employee) || '',
          label: `${employee.firstName} ${employee.lastName}`
        }))
    }
  );

  const jobs = useMemo(
    (): JobOption[] => [
      {
        value: projectId as string,
        label: project.title,
        well: project.well,
        location:
          typeof project.location === 'string'
            ? project.location
            : project.location?.location,
        customer: project.customer?.organizationName || ''
      }
    ],
    [
      projectId,
      project.title,
      project.well,
      project.location,
      project.customer?.organizationName
    ]
  );

  const { data: jobTasks, isLoading: isLoadingTasks } = useQuery(
    ['jobTasks', projectId],
    () => GET_FILTERED_TASKS({ jobId: projectId }),
    {
      enabled: !!projectId,
      staleTime: 60 * 1000
    }
  );
  const taskCount = jobTasks?.data?.length ?? 0;

  const { data: stickyComments = [] } = useQuery(
    ['stickyComments', projectId],
    () => GET_STICKY_COMMENTS_BY_JOB(projectId as string),
    {
      enabled: !!projectId,
      staleTime: 60 * 1000,
      select: data => data?.data || []
    }
  );

  const cardColorClass = getCardStyleForReturnedEquipment(project);
  const isColoredCard = !!cardColorClass;
  const cardTextClass = isColoredCard ? 'text-white' : undefined;
  const cardMutedTextClass = cardTextClass || 'text-800';
  const cardLinkTextClass = cardTextClass || 'text-1000';

  const isEquipmentReturned =
    project.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
    project.jobStage.name.toLowerCase() === STAGE_MAP['Equipment Returned'];

  const isEquipmentShipped =
    project.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_SHIPPED ||
    project.jobStage.name.toLowerCase() === STAGE_MAP['Equipment Shipped'];

  const isOperatorDispatched =
    project.jobStage.name.toLowerCase() === JOB_STAGES.OPERATOR_DISPATCHED ||
    project.jobStage.name.toLowerCase() === STAGE_MAP['Operator Dispatched'];

  const showPostJobReportIcon = isOperatorDispatched || isEquipmentReturned;

  const { data: postJobReportData } = useQuery(
    ['postJobReport', projectId],
    () => GET_LATEST_POST_JOB_REPORT(projectId as string),
    {
      enabled: showPostJobReportIcon && !!projectId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  const postJobReportStatus: 'none' | 'draft' | 'submitted' = useMemo(() => {
    const log = postJobReportData?.data ?? postJobReportData;
    if (!log) return 'none';
    return log?.metadata?.reportStatus === 'submitted' ? 'submitted' : 'draft';
  }, [postJobReportData]);

  const postJobReportTooltip =
    postJobReportStatus === 'submitted'
      ? 'View Post Job Report (Submitted)'
      : postJobReportStatus === 'draft'
      ? 'Continue Post Job Report (Draft)'
      : 'Create Post Job Report';

  const updateJobStatus = useMutation(UPDATE_JOB_STAGE, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update job status');
    }
  });

  const toggleWatchMutation = useMutation(
    () => TOGGLE_WATCH_JOB(projectId as string),
    {
      onSuccess: data => {
        setIsWatching(data.watching);
        queryClient.invalidateQueries('getJobs');
        toast.success(
          data.watching
            ? 'Started watching this job'
            : 'Stopped watching this job'
        );
      },
      onError: () => {
        toast.error('Failed to update watch status');
      }
    }
  );

  const handleWatchToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!projectId || toggleWatchMutation.isLoading) return;
    toggleWatchMutation.mutate();
  };

  const mutateJobPriority = useMutation(UPDATE_JOB_PRIORITY, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        toast.error(error.message || 'Failed to update job priority');
      } else {
        toast.error('Failed to update job priority');
      }
    }
  });

  const handleJobPriority = async (
    jobId: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();
    try {
      await mutateJobPriority.mutateAsync({
        jobId,
        priority: (e.target as HTMLInputElement).checked
      });
      toast.success('Job priority updated successfully');
      queryClient.invalidateQueries('getAllActivityLogs');
    } catch (error: any) {
      toast.error(error.message || 'Error updating job priority');
    }
  };

  const handleStatusChange = async (jobId: string, newStatus: string) => {
    if (newStatus.toLowerCase() === 'closed') {
      const openTasks = project?.tasks?.filter(task => !task.completed);
      if (openTasks && openTasks.length > 0) {
        toast.error(
          'This job has 1 or more open tasks. Please complete the tasks and then change the job status to closed.'
        );
        return;
      }
    }

    if (!getJobStage) {
      toast.error('Job Stage not found');
      return;
    }

    const jobStage = JobStages?.find(
      stage => stage.name.toLowerCase() === newStatus
    );

    try {
      await updateJobStatus.mutateAsync({
        jobId,
        jobStage: { _id: (jobStage as any)?.id || (jobStage as any)?._id }
      });
      toast.success('Job status updated successfully');
    } catch {
      toast.error('Error updating job status');
    }
  };

  useGlobalSocket({
    enabled: Boolean(authUser),
    jobId: projectId,
    onCommentCreated: newComment => {
      console.log('🔥 Comment created in CardViewItem:', newComment);
      if (newComment.jobId === projectId) {
        console.log('🔥 Refetching comments in CardViewItem');
        refetchComments();
        queryClient.invalidateQueries('getJobs');
      }
    },
    onCommentReplyCreated: newReply => {
      console.log('🔥 Reply created in CardViewItem:', newReply);
      if (newReply.jobId === projectId) {
        console.log('🔥 Refetching comments for reply in CardViewItem');
        refetchComments();
        queryClient.invalidateQueries('getJobs');
      }
    },
    onCommentPinUpdated: updatedComment => {
      console.log('Comment pin updated in CardViewItem:', updatedComment);
      if (updatedComment.jobId === projectId) {
        refetchComments();
        queryClient.invalidateQueries(['stickyComments', projectId]);
        queryClient.invalidateQueries('getJobs');
      }
    },
    onCommentDeleted: data => {
      console.log('Comment deleted in CardViewItem:', data);
      if (data.jobId === projectId) {
        refetchComments();
        queryClient.invalidateQueries(['stickyComments', projectId]);
        queryClient.invalidateQueries('getJobs');
      }
    }
  });

  const handleTaskIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDetailsModal(true);
    setScrollToTasks(true);
  };

  const handleCameraIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenImageUpload(true);
  };

  const handleDuplicateIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDuplicateModal(true);
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    if (projectId) {
      queryClient.invalidateQueries(['jobTasks', projectId]);
    }
  };

  const priorityCheckbox = (
    <div className="form-check">
      <input
        className="form-check-input"
        type="checkbox"
        checked={project.isPriority}
        onChange={e => {
          handleJobPriority(projectId as string, e);
          toggleJobPriority(projectId as string);
        }}
        id={`priority-${projectId}`}
      />
      <label
        className={classNames('form-check-label', {
          'text-white': isColoredCard
        })}
        htmlFor={`priority-${projectId}`}
      >
        Priority
      </label>
    </div>
  );

  return (
    <>
      <Card
        className={classNames('h-100 hover-actions-trigger', cardColorClass)}
      >
        <Card.Body
          className={classNames({
            'project-colored-card': isColoredCard
          })}
        >
          <div className="d-flex align-items-center">
            <h4
              className={classNames('mb-2 line-clamp-1 lh-sm flex-1 me-5', {
                'text-white': isColoredCard
              })}
            >
              {project.title}
            </h4>
            <div className="hover-actions top-0 end-0 mt-4 me-4">
              {(canUpdateJobStage || canUpdateMarkClosed) && (
                <Dropdown align="start">
                  <Dropdown.Toggle
                    variant="falcon-default"
                    className="btn-icon dropdown-caret-none"
                  >
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {canUpdateJobStage && (
                      <Dropdown.Item onClick={() => setShowStageModal(true)}>
                        Job Stage
                      </Dropdown.Item>
                    )}
                    {canUpdateMarkClosed && (
                      <Dropdown.Item
                        onClick={() =>
                          handleStatusChange(
                            projectId as string,
                            project.jobStage.name === 'closed'
                              ? 'cancelled'
                              : 'closed'
                          )
                        }
                        className={classNames({
                          'text-danger': project.jobStage.name !== 'cancelled'
                        })}
                      >
                        Mark as{' '}
                        {project.jobStage.name === 'closed'
                          ? 'Cancelled'
                          : 'Closed'}
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <Button
                variant="primary"
                className="btn-icon flex-shrink-0"
                onClick={() => setOpenDetailsModal(true)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>

          <Badge
            variant="phoenix"
            bg={project?.jobStage?.name === 'New Job' ? 'success' : 'secondary'}
            className="fs-10 mb-4"
          >
            {project?.jobType?.name}
          </Badge>

          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon
              icon={faMapLocation}
              className={classNames(
                'me-2 fs-9 fw-extra-bold',
                cardTextClass || 'text-700'
              )}
            />
            <p
              className={classNames(
                'fw-bold mb-0 text-truncate lh-1',
                cardTextClass
              )}
            >
              Location:{' '}
              <CustomerView
                customer={project?.location?.location}
                className={cardTextClass || 'text-primary'}
              />
            </p>
          </div>

          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon
              icon={faMapLocationDot}
              className={classNames('me-2 fs-9 fw-extra-bold', {
                'text-white': isColoredCard,
                'text-700': !isColoredCard && !project.isPriority
              })}
            />
            <p
              className={classNames(
                'fw-bold mb-0 text-truncate lh-1',
                cardTextClass
              )}
            >
              Well:{' '}
              <CustomerView
                customer={project?.well}
                className={cardTextClass || 'text-primary'}
              />
            </p>
          </div>

          <div>
            <DateFormatter
              shipmentDate={project?.expectedEquipmentShipDate}
              className={cardTextClass}
            />
            <div className="d-flex align-items-center mt-2">
              <p className={classNames('mb-0 fw-bold fs-9', cardTextClass)}>
                Job Number:
                <span
                  className={classNames('fw-semi-bold ms-1', {
                    'text-white': isColoredCard,
                    'text-600': !isColoredCard
                  })}
                >
                  {project?.jobNumber ? project.jobNumber : 'No number set'}
                </span>
              </p>
            </div>
            <div className="d-flex align-items-center mt-2">
              <p className={classNames('mb-0 fw-bold fs-9', cardTextClass)}>
                Job Description:
                <span
                  className={classNames('fw-semi-bold ms-1', {
                    'text-white': isColoredCard,
                    'text-600': !isColoredCard
                  })}
                >
                  {project?.description
                    ? shortenText(project.description, 30)
                    : ''}
                </span>
              </p>
            </div>
          </div>

          <StickyComments
            stickyComments={stickyComments}
            userId={getEntityId(authUser) || ''}
            isPriority={project?.isPriority}
            textClass={cardTextClass}
          />

          <div className="d-flex mt-4 flex-column">
            <h4 className={classNames('mb-2 fw-bold fs-9', cardTextClass)}>
              Latest Comment:
            </h4>
            {latestComment ? (
              <Row className="justify-content-between g-0 pb-3 align-items-start">
                <Col xs={12} lg={12}>
                  <div className="d-flex justify-content-between align-items-start">
                    <p
                      className={classNames(
                        'fs-9 mb-0 flex-grow-1',
                        {
                          'mb-2': latestComment?.content
                        },
                        cardMutedTextClass
                      )}
                    >
                      <Link
                        to={latestComment?.creator?.username}
                        className={classNames(
                          'fw-semi-bold text-decoration-none',
                          cardLinkTextClass
                        )}
                      >
                        {getEntityId(latestComment?.creator) ===
                        getEntityId(authUser)
                          ? 'You'
                          : `${latestComment?.creator?.firstName} ${latestComment?.creator?.lastName}`}
                      </Link>{' '}
                      <span
                        className={`badge bg-${
                          latestComment?.commentType === CommentType.Field
                            ? 'success'
                            : 'info'
                        }`}
                      >
                        {latestComment?.commentType}
                      </span>
                    </p>
                  </div>
                </Col>
                <Col
                  xs={{ span: 12, order: 1 }}
                  sm={{ span: 'auto', order: 0 }}
                >
                  <p
                    className={classNames(
                      'fw-semi-bold fs-10 mb-0',
                      cardMutedTextClass
                    )}
                  >
                    <HumanReadableTime
                      date={
                        new Date(latestComment?.createdAt as unknown as Date)
                      }
                    />
                  </p>
                </Col>
                {latestComment?.content && (
                  <Col xs={12}>
                    <div className="position-relative">
                      <p
                        className={classNames('mb-0', cardTextClass)}
                        style={{
                          maxHeight: expandedComment ? 'none' : '6rem',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: expandedComment ? 'unset' : 4,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.5rem',
                          wordBreak: 'break-word'
                        }}
                      >
                        {latestComment.content}
                      </p>
                      {latestComment.content.length > 150 && (
                        <button
                          type="button"
                          className={`text-decoration-none btn btn-link btn-sm p-0 ${
                            isColoredCard ? 'text-white' : 'text-primary'
                          } fs-9 mt-1`}
                          onClick={() => setExpandedComment(!expandedComment)}
                        >
                          {expandedComment ? 'Read less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            ) : (
              <Row className="justify-content-between g-0 py-3 align-items-start">
                <Col xs={12}>
                  <p className={classNames(cardMutedTextClass)}>
                    No Comment Added
                  </p>
                </Col>
              </Row>
            )}
          </div>

          <div className="d-flex flex-row justify-content-center gap-3 mt-3">
            <div
              className="position-relative cursor-pointer"
              onClick={() => setOpenDetailsModal(true)}
              title="View Comments"
            >
              <FontAwesomeIcon
                icon={faComments}
                className={`${
                  isColoredCard ? 'text-white' : 'text-primary'
                } fs-7`}
              />
              {totalCommentCount > 0 && (
                <span
                  className={classNames(
                    'position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger fs-10',
                    {
                      'text-white': isColoredCard
                    }
                  )}
                >
                  {totalCommentCount > 99 ? '99+' : totalCommentCount}
                </span>
              )}
            </div>

            <div
              className="position-relative cursor-pointer"
              onClick={handleCameraIconClick}
              title="Add Images"
            >
              <FontAwesomeIcon
                icon={faCamera}
                className={cardTextClass || 'text-secondary'}
              />
              {project.files && project.files.length > 0 && (
                <span
                  className={classNames(
                    'position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary fs-10',
                    {
                      'text-white': isColoredCard,
                      'text-dark': !isColoredCard
                    }
                  )}
                >
                  {project.files.length > 99 ? '99+' : project.files.length}
                </span>
              )}
            </div>

            <div
              className="position-relative cursor-pointer"
              onClick={() => setOpenSharePoint(true)}
              title="SharePoint Files"
            >
              <FontAwesomeIcon icon={faFolder} className="text-success fs-7" />
            </div>

            <div
              className="position-relative cursor-pointer"
              onClick={handleTaskIconClick}
              title="View Tasks"
            >
              <FontAwesomeIcon
                icon={faListCheck}
                className={`${isColoredCard ? 'text-white' : 'text-info'} fs-7`}
              />
              {taskCount > 0 && (
                <span
                  className={classNames(
                    'position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning fs-10',
                    {
                      'text-white': isColoredCard,
                      'text-dark': !isColoredCard
                    }
                  )}
                >
                  {taskCount > 99 ? '99+' : taskCount}
                </span>
              )}
            </div>

            <div
              className="position-relative cursor-pointer"
              onClick={handleDuplicateIconClick}
              title="Duplicate Job"
            >
              <FontAwesomeIcon
                icon={faCopy}
                className={`${
                  isColoredCard ? 'text-white' : 'text-primary'
                } fs-7`}
              />
            </div>

            <div
              className="position-relative cursor-pointer"
              onClick={() => setOpenToolWorkOrder(true)}
              title="Tool Work Order"
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className={cardTextClass || 'text-secondary'}
              />
            </div>

            <div
              className="position-relative cursor-pointer"
              onClick={handleWatchToggle}
              title={
                toggleWatchMutation.isLoading
                  ? 'Updating watch status...'
                  : isWatching
                  ? 'Stop watching this job'
                  : 'Start watching this job'
              }
            >
              <FontAwesomeIcon
                icon={faEye}
                className={classNames('fs-7', {
                  'text-warning': isWatching,
                  'text-muted': !isWatching,
                  'opacity-50': toggleWatchMutation.isLoading
                })}
              />
            </div>

            {showPostJobReportIcon && (
              <div
                className="position-relative cursor-pointer"
                onClick={() => setOpenPostJobReport(true)}
                title={postJobReportTooltip}
              >
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className={
                    postJobReportStatus === 'submitted'
                      ? 'pjr-report-icon-submitted fs-7'
                      : postJobReportStatus === 'draft'
                      ? 'pjr-report-icon-draft fs-7'
                      : 'text-warning fs-7'
                  }
                />
              </div>
            )}
          </div>

          {isEquipmentReturned && (
            <div className="mt-3 d-flex justify-content-between align-items-center border-top pt-3">
              <div
                className={classNames('fs-9', {
                  'text-white': isColoredCard
                })}
              >
                <strong>Days Since Returned:</strong> {project.daysReturned}
              </div>
              {priorityCheckbox}
            </div>
          )}
          {(isEquipmentShipped || isOperatorDispatched) && (
            <div className="mt-3 d-flex justify-content-end align-items-center border-top pt-3">
              {priorityCheckbox}
            </div>
          )}
        </Card.Body>
      </Card>

      <CardViewModal
        show={openDetailsModal}
        handleClose={() => setOpenDetailsModal(false)}
        project={project}
        canEditJob={canUpdateJob}
        canAddCommentsField={canAddCommentsField}
        canAddShopCommentsField={canAddShopCommentsField}
        canAddJobStageCommentsField={canAddJobStageCommentsField}
        isLoadingTasks={isLoadingTasks}
        jobTasks={jobTasks?.data}
        comments={currentComments}
        setComments={setComments}
        scrollToTasks={scrollToTasks}
        setScrollToTasks={setScrollToTasks}
      />

      <ChangeJobStageModal
        show={showStageModal}
        onHide={() => setShowStageModal(false)}
        job={project}
        jobStages={JobStages}
      />

      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          handleClose={handleCloseTaskModal}
          employees={employees || []}
          jobs={jobs}
        />
      )}

      {openToolWorkOrder && (
        <ToolWorkOrderModal
          show={openToolWorkOrder}
          handleClose={() => setOpenToolWorkOrder(false)}
          job={project}
          jobStages={JobStages}
        />
      )}

      <ImageUploadModal
        show={openImageUpload}
        onHide={() => setOpenImageUpload(false)}
        jobId={projectId as string}
        files={project?.files || []}
        setHasUpdated={updated => {
          if (updated) queryClient.invalidateQueries('getJobs');
        }}
        jobNumbers={project?.jobNumber ? [project.jobNumber] : []}
        companyNames={
          project?.customer?.organizationName
            ? [project.customer.organizationName]
            : []
        }
      />

      <SharePointModal
        show={openSharePoint}
        onHide={() => setOpenSharePoint(false)}
        jobNumbers={[project?.jobNumber as string]}
        companyNames={
          project?.customer?.organizationName
            ? [project.customer.organizationName.trim()]
            : []
        }
      />

      <DuplicateJobModal
        show={openDuplicateModal}
        onHide={() => setOpenDuplicateModal(false)}
        job={project}
        onSuccess={() => {
          queryClient.invalidateQueries('getJobs');
        }}
      />

      {openPostJobReport && (
        <PostJobReportModal
          show={openPostJobReport}
          onHide={() => {
            setOpenPostJobReport(false);
            queryClient.invalidateQueries(['postJobReport', projectId]);
          }}
          job={project}
        />
      )}
    </>
  );
};

export default CardViewItem;
